import React from 'react'
import Slider from 'react-slick'
import vector from '../../images/vector.svg'

import './carousel.scss'

/**
 * A reusable arrow button component for carousel navigation.
 *
 * @param {string} props.className - Additional CSS classes to apply to the button.
 * @param {Object} props.style - Custom inline styles to apply to the button.
 * @param {Function} props.onClick - The callback function to execute when the button is clicked.
 *
 * @returns {JSX.Element} A React component representing a navigational arrow button.
 */
function Arrow({ className, style, onClick }) {
  return (
    <button
      className={`${className} icon-button`}
      style={{ ...style }}
      onClick={onClick}
    >
      <img src={vector} alt="Arrow" />
    </button>
  )
}

/**
 * A versatile carousel component for displaying a list of items.
 *
 * @param {Array<JSX.Element>} props.data - An array of JSX elements representing the items to be displayed in the carousel.
 * @param {Object} props.settings - Carousel settings including properties like 'arrows', 'infinite', 'centerMode', and more.
 * @param {Array} props.responsiveSettings - An array of responsive settings for the carousel (optional, default is an empty array).
 * @param {Array} props.autoPlay - A boolean to use the "default" autoplay settings (optional, default is false).
 * @returns {JSX.Element} A React component rendering a carousel with the provided data and settings.
 */
export const Carousel = ({
  data,
  settings = {},
  responsiveSettings = [],
  gradiant = { show: false },
  autoPlay = false,
}) => {
  const carouselSettings = {
    arrows: false,
    infinite: true,
    centerMode: true,
    variableWidth: true,
    cssEase: 'ease',

    ...(settings.arrows && {
      nextArrow: <Arrow next />,
      prevArrow: <Arrow prev />,
    }),

    ...(autoPlay && {
      autoplay: true,
      pauseOnHover: true,
      speed: 1000,
      autoplaySpeed: 4000,
    }),

    ...settings,
  }

  const gradiantSettings = {
    show: false,
    color: 'off-white',

    ...gradiant,
  }

  return (
    <div className="carousel-container">
      <Slider {...carouselSettings} responsive={responsiveSettings}>
        {data}
      </Slider>

      {gradiantSettings.show && (
        <div>
          <div className="innerContainer">
            <div className={`gradient left ${gradiantSettings.color}`} />
            <div className={`gradient right ${gradiantSettings.color} `} />
          </div>
        </div>
      )}
    </div>
  )
}
